/**
 * Warns when the AI gateway has nothing it can route to, per
 * `GET /admin/ai-gateway/status`'s `unroutable_reason`.
 *
 * A null reason means the gateway is healthy (or its state simply isn't
 * known yet) and nothing is rendered.
 */

import type { ReactElement } from 'react';
import type { GatewayStatus } from '@services/aiModelsService';

const REASON_MESSAGES: Record<string, string> = {
  gateway_disabled: 'The AI gateway is disabled — no requests will be routed.',
  registry_empty: 'No models are registered in the AI model registry — nothing is routable.',
  upstreams_unconfigured:
    'No upstream is configured for any registered model — nothing is routable.',
};

interface GatewayStatusBannerProps {
  status: GatewayStatus | undefined;
}

export function GatewayStatusBanner({ status }: GatewayStatusBannerProps): ReactElement | null {
  if (!status || status.unroutable_reason === null) return null;

  const message =
    REASON_MESSAGES[status.unroutable_reason] ??
    `The gateway has nothing routable (${status.unroutable_reason}).`;

  return (
    <div className="ai-models__banner" role="alert">
      {message}
    </div>
  );
}
